// The flagged value behind each "[verify]" marker, for the review card
// (VITE_REVIEW_CARD_ENABLED). Same rules as the iOS app
// (ios-native Domain/VerifyFlags.swift), so a note reviews the same way on
// both clients.
//
// The note generator puts "[verify]" right after a value it could not hear
// clearly (lib/verifyMarkers.ts). The value is not delimited, so the clause
// just before the marker is taken as the value: from the last ", " / "; " /
// ": " / ". " / " - " (or the start of the line, after its bullet) up to the
// marker. A marker at the start of a clause takes the clause after it.
//
// - Accept removes the marker and the spaces before it, keeping the value
//   (removeVerifyMarker).
// - Edit replaces the value with the new text and removes the marker in the
//   same save.
// - Remove (or an Edit saved empty) takes the value out of the note.

import { isEmptyLine, tidyJoin } from "./noteSentences";
import {
  removeVerifyMarker,
  stripVerifyMarkers,
  verifyMarkerPattern,
} from "./verifyMarkers";

export interface VerifyFlag {
  // 0-based position of the marker in the text (same numbering as the chips
  // and the editor).
  index: number;
  // The value exactly as written in the Markdown, and where it is.
  rawValue: string;
  valueStart: number;
  valueEnd: number;
  markerStart: number;
  markerEnd: number;
  // Plain text of the line around the value, without markers or Markdown.
  before: string;
  value: string;
  after: string;
}

const LIST_PREFIX = /^\s*(?:[-*+•]|\d+[.)]|☐|\[ \]|#{1,6})\s+/u;
// Clause boundaries before a value, optionally after closing emphasis.
const BOUNDARY =
  /(?:[,;:](?:\*\*|__|\*|_)?|(?<!\d)\.(?:\*\*|__)?|\s[-–—])\s+/gu;
const BLANK = /[ \t]/;

const lastBoundaryEnd = (text: string, from: number): number | null => {
  let end: number | null = null;
  const pattern = new RegExp(BOUNDARY.source, "gu");
  pattern.lastIndex = from;
  for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
    end = match.index + match[0].length;
    if (match[0].length === 0) pattern.lastIndex += 1;
  }
  return end;
};

const firstBoundaryStart = (text: string, from: number): number | null => {
  const pattern = new RegExp(BOUNDARY.source, "gu");
  pattern.lastIndex = from;
  const match = pattern.exec(text);
  return match ? match.index : null;
};

// Plain text of a line fragment: markers, list prefix (at line start) and
// Markdown emphasis, links and code marks removed.
const plain = (raw: string, leading = false): string => {
  let text = raw.replace(verifyMarkerPattern(), "");
  if (leading) text = text.replace(LIST_PREFIX, "");
  return text
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\*\*|__|`/g, "")
    .replace(
      /(^|[^\p{L}\p{N}])[*_](?=\S)|(?<=\S)[*_](?=$|[^\p{L}\p{N}])/gu,
      "$1",
    )
    .replace(/\\([\\[\]*_`])/g, "$1")
    .replace(/[ \t]{2,}/g, " ");
};

// Every "[verify]" marker in the text with its value and line.
export const extractVerifyFlags = (text: string): VerifyFlag[] => {
  const markers = [...text.matchAll(verifyMarkerPattern())];
  const flags: VerifyFlag[] = [];
  markers.forEach((marker, index) => {
    const markerStart = marker.index ?? 0;
    const markerEnd = markerStart + marker[0].length;
    const lineStart = text.lastIndexOf("\n", markerStart - 1) + 1;
    const newline = text.indexOf("\n", markerEnd);
    let lineEnd = newline === -1 ? text.length : newline;
    if (text[lineEnd - 1] === "\r") lineEnd -= 1;
    // The clause starts after the previous marker on this line, if any.
    const previous = markers[index - 1];
    const previousEnd = previous
      ? (previous.index ?? 0) + previous[0].length
      : -1;
    const floor = Math.max(previousEnd, lineStart);
    const prefix = text.slice(floor, markerStart);
    let start = 0;
    if (floor === lineStart) {
      const list = LIST_PREFIX.exec(prefix);
      if (list) start = list[0].length;
    }
    start = lastBoundaryEnd(prefix, start) ?? start;
    let end = prefix.length;
    while (end > start && BLANK.test(prefix[end - 1] ?? "")) end -= 1;
    while (start < end && BLANK.test(prefix[start] ?? "")) start += 1;
    // A clause that ends in a bold span ("Rash with **sulfa** [verify]"):
    // the bold words are the value.
    const bold = /\*\*([^*]+)\*\*$/.exec(prefix.slice(start, end));
    if (bold && bold.index > 0) start += bold.index;
    let valueStart = floor + start;
    let valueEnd = floor + end;
    if (valueEnd === valueStart) {
      // Marker at the start of a clause: take the clause after it.
      const tail = text.slice(markerEnd, lineEnd);
      let from = 0;
      while (from < tail.length && BLANK.test(tail[from] ?? "")) from += 1;
      const stop = firstBoundaryStart(tail, from) ?? tail.length;
      let to = stop;
      while (to > from && BLANK.test(tail[to - 1] ?? "")) to -= 1;
      valueStart = markerEnd + from;
      valueEnd = markerEnd + Math.max(from, to);
    }
    const rawValue = text.slice(valueStart, valueEnd);
    const afterStart = Math.max(markerEnd, valueEnd);
    flags.push({
      index,
      rawValue,
      valueStart,
      valueEnd,
      markerStart,
      markerEnd,
      before: plain(
        text.slice(lineStart, Math.min(valueStart, markerStart)),
        true,
      ).trimStart(),
      value: plain(rawValue).trim(),
      after: plain(text.slice(afterStart, lineEnd)).trimEnd(),
    });
  });
  return flags;
};

// A pre-chart item the visit never mentioned ("Ramipril 10 mg daily
// (pre-chart) [verify]").
export const isPreChartFlag = (flag: Pick<VerifyFlag, "rawValue">): boolean =>
  /\(pre-chart\)\s*$/i.test(flag.rawValue);

export type VerifyAction =
  | { type: "accept" }
  | { type: "edit"; value: string }
  // The flagged value is not right at all (or a pre-chart item the patient
  // no longer has or takes): the value and its marker go, with the
  // separator before it in a list, and an empty line with them.
  | { type: "remove" };

// The text after resolving marker `index`. Unchanged when there is no such
// marker. An edit saved empty removes the value (as Remove does).
export const applyVerifyAction = (
  text: string,
  index: number,
  action: VerifyAction,
): string => {
  if (action.type === "accept") return removeVerifyMarker(text, index);
  if (action.type === "remove") {
    const item = extractVerifyFlags(text).find((f) => f.index === index);
    if (!item) return text;
    // A marker with no value to take: only the marker goes.
    if (item.valueEnd <= item.valueStart)
      return removeVerifyMarker(text, index);
    let start = item.valueStart;
    let end = Math.max(item.markerEnd, item.valueEnd);
    const before = text.slice(0, start);
    const valueLine = text.lastIndexOf("\n", start - 1) + 1;
    const head = text.slice(valueLine, start).replace(LIST_PREFIX, "");
    // A whole sentence ("T 38.1 °C. BP 124/80 mmHg [verify]. SpO2 96%."):
    // it goes with its full stop and the space after it, not leaving "..".
    const terminator = /^[ \t]*[.!?。！？]+(?:\*\*|__|[)"'”’])*[ \t]*/u.exec(
      text.slice(end),
    );
    const sentenceStart =
      !head.trim() || /[.!?。！？](?:\*\*|__|[)"'”’])*[ \t]+$/u.test(head);
    const separator = /(?:,|;|\s+and)\s*$/.exec(before);
    if (terminator && sentenceStart) {
      end += terminator[0].length;
      const newline = text.indexOf("\n", end);
      const lineEnd = newline === -1 ? text.length : newline;
      // The line's last sentence: the space before it goes instead.
      if (!text.slice(end, lineEnd).trim())
        while (start > valueLine && BLANK.test(text[start - 1] ?? ""))
          start -= 1;
    } else if (separator) start = separator.index;
    else {
      // First item of a list: take the separator after it instead.
      const after = /^\s*(?:,|;)\s*/.exec(text.slice(end));
      if (after) end += after[0].length;
    }
    // No space left before the punctuation that followed the item.
    if (/^[.,;]/.test(text.slice(end)))
      while (start > 0 && BLANK.test(text[start - 1] ?? "")) start -= 1;
    // The line's text started with the removed value: what is left starts
    // in uppercase, as it did.
    const capitalize = /^\p{Lu}/u.test(text.slice(item.valueStart));
    const next = tidyJoin(text.slice(0, start) + text.slice(end), start, {
      capitalize,
    });
    // The line left with only a bullet, a number or a label ("Allergies -",
    // "To discuss:") goes too.
    const lineStart = next.lastIndexOf("\n", start - 1) + 1;
    const newline = next.indexOf("\n", start);
    const lineEnd = newline === -1 ? next.length : newline;
    const line = next.slice(lineStart, lineEnd);
    if (isEmptyLine(line))
      return (
        next.slice(0, Math.max(0, newline === -1 ? lineStart - 1 : lineStart)) +
        next.slice(newline === -1 ? lineEnd : lineEnd + 1)
      );
    return next;
  }
  const value = action.value.replace(/\s*\n\s*/g, " ").trim();
  if (!value) return applyVerifyAction(text, index, { type: "remove" });
  const flag = extractVerifyFlags(text).find((item) => item.index === index);
  if (!flag) return text;
  if (flag.valueEnd === flag.valueStart) {
    // Nothing to replace: the new value takes the marker's place.
    let start = flag.markerStart;
    while (start > 0 && BLANK.test(text[start - 1] ?? "")) start -= 1;
    const gap = start > 0 && text[start - 1] !== "\n" ? " " : "";
    return text.slice(0, start) + gap + value + text.slice(flag.markerEnd);
  }
  if (flag.valueStart < flag.markerStart) {
    // value … [verify] → new value (the spaces before the marker go too).
    return text.slice(0, flag.valueStart) + value + text.slice(flag.markerEnd);
  }
  // [verify] value → new value.
  return text.slice(0, flag.markerStart) + value + text.slice(flag.valueEnd);
};

// "Accept all" (VITE_REVIEW_COPY_ANYWAY_ENABLED): every value stays as
// written and every marker goes, as accepting each one in turn would do.
export const acceptAllVerifyFlags = (text: string): string =>
  stripVerifyMarkers(text);

// The Markdown with each flagged value wrapped in a placeholder element the
// renderer turns into a highlight (FileResultRendererMD), and the markers
// removed. `offset` is the index of the fragment's first marker in the
// whole note. An empty value keeps a placeholder with no text.
export const VERIFY_FLAG_ATTRIBUTE = "data-verify-flag";
export const markVerifyFlags = (markdown: string, offset = 0): string => {
  const flags = extractVerifyFlags(markdown);
  if (!flags.length) return markdown;
  let out = "";
  let last = 0;
  for (const flag of flags) {
    const open = `<span ${VERIFY_FLAG_ATTRIBUTE}="${offset + flag.index}">`;
    if (flag.valueStart < flag.markerStart) {
      out += `${markdown.slice(last, flag.valueStart)}${open}${flag.rawValue}</span>`;
      last = flag.markerEnd;
    } else {
      let start = flag.markerStart;
      while (start > last && BLANK.test(markdown[start - 1] ?? "")) start -= 1;
      const gap = start < flag.markerStart ? " " : "";
      out += `${markdown.slice(last, start)}${gap}${open}${flag.rawValue}</span>`;
      last = Math.max(flag.markerEnd, flag.valueEnd);
    }
  }
  return out + markdown.slice(last);
};

export interface VerifyFlagOptions {
  // Accessible name prefix ("Unclear value") and tooltip.
  label: string;
  title: string;
  // Shown for a flag whose value could not be found ("verify").
  emptyLabel: string;
  // Highlights are buttons (open the review card) only when the note can be
  // edited; otherwise they are marks.
  interactive?: boolean;
}

// v3.2 "unclear audio" highlight: warn tint with an amber underline, wraps
// across lines like the text around it. Kept as a literal so Tailwind
// generates the classes.
export const VERIFY_FLAG_CLASS =
  "verify-flag rounded-[4px] bg-v3-warn-tint px-0.5 text-inherit [box-decoration-break:clone] [-webkit-box-decoration-break:clone] shadow-[inset_0_-2px_0_var(--v3-warn)]";
const VERIFY_FLAG_INTERACTIVE_CLASS =
  "cursor-pointer hover:bg-v3-warn/25 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-warn data-[active=true]:bg-v3-warn/30";

// Turns the placeholders from markVerifyFlags into highlights in a rendered
// note. Returns how many it turned.
export const decorateVerifyFlags = (
  root: HTMLElement,
  { label, title, emptyLabel, interactive = false }: VerifyFlagOptions,
): number => {
  const doc = root.ownerDocument;
  const placeholders = [
    ...root.querySelectorAll<HTMLElement>(`span[${VERIFY_FLAG_ATTRIBUTE}]`),
  ];
  for (const placeholder of placeholders) {
    const mark = doc.createElement("mark");
    const index = placeholder.getAttribute(VERIFY_FLAG_ATTRIBUTE) ?? "0";
    mark.className = interactive
      ? `${VERIFY_FLAG_CLASS} ${VERIFY_FLAG_INTERACTIVE_CLASS}`
      : VERIFY_FLAG_CLASS;
    mark.setAttribute("data-verify-index", index);
    mark.title = title;
    mark.append(...placeholder.childNodes);
    if (!mark.textContent?.trim()) mark.textContent = emptyLabel;
    if (interactive) {
      mark.setAttribute("role", "button");
      mark.setAttribute("tabindex", "0");
      mark.setAttribute(
        "aria-label",
        `${label}: ${mark.textContent?.trim() ?? ""}`,
      );
    }
    placeholder.replaceWith(mark);
  }
  return placeholders.length;
};
